import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../api/axios';

const statusOptions = ['ACTIVE', 'INACTIVE'];

export default function DepartmentForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    status: 'ACTIVE',
  });
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDepartment = async () => {
      if (!isEdit) {
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/departments/${id}`);
        const department = response.data;
        setForm({
          code: department.code || '',
          name: department.name || '',
          description: department.description || '',
          status: department.status || 'ACTIVE',
        });
      } catch (err) {
        const message = err.response?.data?.message || 'No se pudo cargar el departamento.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadDepartment();
  }, [id, isEdit]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setIsSaving(true);

    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      status: form.status,
    };

    try {
      if (isEdit) {
        await axios.patch(`/departments/${id}`, payload);
      } else {
        await axios.post('/departments', payload);
      }
      navigate('/home/departamentos');
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo guardar el departamento.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Editar departamento' : 'Nuevo departamento'}
        </h1>
        <p className="text-sm text-gray-500">
          {isEdit ? 'Actualiza la informacion del departamento.' : 'Registra un nuevo departamento.'}
        </p>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-sm">
        {isLoading ? (
          <div className="text-sm text-gray-500">Cargando departamento...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-gray-600">Codigo</label>
                <input
                  type="text"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Nombre</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Estado</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-gray-600">Descripcion</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows="3"
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
            </div>

            {error && <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-60"
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/home/departamentos')}
                className="rounded-lg border border-gray-200 px-4 py-2 text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
